import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format, formatDistanceToNow } from "date-fns";
import {
  MapPin, Clock, Calendar, CheckCircle, Circle, Image as ImageIcon,
  MessageCircle, Heart, Compass, Star, Target, FileText, X, User, Download
} from "lucide-react";
import ShareLinkManager from "@/components/ShareLinkManager";
import { ExportButton } from "@/components/ExportButton";

const CATEGORIES = {
  travel: { icon: MapPin, color: "#E07A5F", label: "Travel" },
  adventures: { icon: Compass, color: "#81B29A", label: "Adventures" },
  experiences: { icon: Star, color: "#F2CC8F", label: "Experiences" },
  goals: { icon: Target, color: "#264653", label: "Goals" },
  romance: { icon: Heart, color: "#EF476F", label: "Romance" },
};

export const TripSummary = ({ bucketItemId, isOpen, onClose }) => {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoUrls, setPhotoUrls] = useState({});

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/items/${bucketItemId}/summary`);
      setSummary(res.data);
      
      // Parallel photo fetches (was serial → slow grid render)
      const fetches = (res.data.photos || []).map(async (photo) => {
        try {
          const photoRes = await axios.get(`${API}/photos/${photo.id}/download`, { responseType: 'blob' });
          return [photo.id, URL.createObjectURL(photoRes.data)];
        } catch (e) { return null; }
      });
      const settled = await Promise.all(fetches);
      setPhotoUrls(Object.fromEntries(settled.filter(Boolean)));
    } catch (error) {
      toast.error("Failed to load trip summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && bucketItemId) {
      fetchSummary();
    }
    
    return () => {
      Object.values(photoUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [isOpen, bucketItemId]);

  if (!isOpen) return null;

  const category = summary?.item ? CATEGORIES[summary.item.category] || CATEGORIES.experiences : null;
  const CategoryIcon = category?.icon || Star;
  const itineraryProgress = summary?.itinerary_stats?.total > 0 
    ? Math.round((summary.itinerary_stats.completed / summary.itinerary_stats.total) * 100) 
    : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-card border-none rounded-2xl p-0 max-h-[90vh] overflow-hidden">
        <ScrollArea className="max-h-[90vh]">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-pulse text-primary text-lg font-serif">Loading trip summary...</div>
            </div>
          ) : summary ? (
            <div>
              {/* Header with gradient */}
              <div 
                className="relative p-8 pb-12"
                style={{ background: `linear-gradient(135deg, ${category?.color}20 0%, ${category?.color}05 100%)` }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: `${category?.color}20` }}
                  >
                    <CategoryIcon className="w-5 h-5" style={{ color: category?.color }} />
                  </div>
                  <span 
                    className="text-sm font-semibold px-3 py-1 rounded-full"
                    style={{ background: `${category?.color}15`, color: category?.color }}
                  >
                    {category?.label}
                  </span>
                  {summary.item.completed && (
                    <span className="text-sm font-semibold px-3 py-1 rounded-full bg-secondary/15 text-secondary">
                      ✓ Completed
                    </span>
                  )}
                </div>

                <h1 className="font-serif text-3xl font-medium text-charcoal mb-2">
                  {summary.item.title}
                </h1>
                
                {summary.item.description && (
                  <p className="text-muted-foreground">{summary.item.description}</p>
                )}

                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-muted-foreground">
                  <span>by {summary.item.created_by_name}</span>
                  {summary.item.target_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {format(new Date(summary.item.target_date), "MMMM d, yyyy")}
                    </span>
                  )}
                </div>
              </div>

              {/* Stats Bar */}
              <div className="grid grid-cols-3 gap-4 px-8 -mt-6">
                <div className="bg-card rounded-xl p-4 shadow-card text-center">
                  <div className="text-2xl font-serif font-medium text-charcoal">
                    {summary.itinerary_stats?.total || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Activities</div>
                </div>
                <div className="bg-card rounded-xl p-4 shadow-card text-center">
                  <div className="text-2xl font-serif font-medium text-charcoal">
                    {summary.photo_count || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Photos</div>
                </div>
                <div className="bg-card rounded-xl p-4 shadow-card text-center">
                  <div className="text-2xl font-serif font-medium text-charcoal">
                    {summary.comment_count || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Comments</div>
                </div>
              </div>

              {/* Content Sections */}
              <div className="p-8 space-y-8">
                {/* Itinerary Section */}
                {summary.itinerary && summary.itinerary.length > 0 && (
                  <section>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-serif text-xl font-medium text-charcoal flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        Itinerary
                      </h2>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{itineraryProgress}% complete</span>
                      </div>
                    </div>
                    
                    <Progress value={itineraryProgress} className="h-2 mb-4" />
                    
                    <div className="space-y-3">
                      {summary.itinerary.map((activity, index) => (
                        <div 
                          key={activity.id}
                          className={`flex items-start gap-3 p-4 rounded-xl bg-background ${
                            activity.completed ? "opacity-60" : ""
                          }`}
                        >
                          <div className="flex-shrink-0 mt-0.5">
                            {activity.completed ? (
                              <CheckCircle className="w-5 h-5 text-secondary" />
                            ) : (
                              <Circle className="w-5 h-5 text-muted-foreground" />
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <h4 className={`font-medium text-charcoal ${activity.completed ? "line-through" : ""}`}>
                              {activity.title}
                            </h4>
                            
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-muted-foreground">
                              {activity.date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3.5 h-3.5" />
                                  {format(new Date(activity.date), "MMM d")}
                                </span>
                              )}
                              {activity.time && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3.5 h-3.5" />
                                  {activity.time}
                                </span>
                              )}
                              {activity.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3.5 h-3.5" />
                                  {activity.location}
                                </span>
                              )}
                            </div>
                            
                            {activity.notes && (
                              <p className="text-sm text-muted-foreground mt-2">{activity.notes}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Photos Section */}
                {summary.photos && summary.photos.length > 0 && (
                  <section>
                    <h2 className="font-serif text-xl font-medium text-charcoal flex items-center gap-2 mb-4">
                      <ImageIcon className="w-5 h-5 text-primary" />
                      Memories
                    </h2>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {summary.photos.map((photo) => (
                        <div
                          key={photo.id}
                          className="aspect-square rounded-xl overflow-hidden bg-muted"
                        >
                          {photoUrls[photo.id] ? (
                            <img
                              src={photoUrls[photo.id]}
                              alt={photo.original_filename}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ImageIcon className="w-8 h-8 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Comments Section */}
                {summary.comments && summary.comments.length > 0 && (
                  <section>
                    <h2 className="font-serif text-xl font-medium text-charcoal flex items-center gap-2 mb-4">
                      <MessageCircle className="w-5 h-5 text-primary" />
                      Discussion
                    </h2>
                    
                    <div className="space-y-3">
                      {summary.comments.map((comment) => (
                        <div key={comment.id} className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1">
                            <div className="bg-background rounded-xl px-4 py-2">
                              <p className="text-sm text-charcoal">{comment.text}</p>
                            </div>
                            <div className="flex items-center gap-2 mt-1 px-2">
                              <span className="text-xs text-muted-foreground">
                                {comment.created_by_name}
                              </span>
                              <span className="text-xs text-muted-foreground">•</span>
                              <span className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Empty state if no content */}
                {(!summary.itinerary || summary.itinerary.length === 0) && 
                 (!summary.photos || summary.photos.length === 0) && 
                 (!summary.comments || summary.comments.length === 0) && (
                  <div className="text-center py-8">
                    <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                      <Star className="w-10 h-10 text-primary" />
                    </div>
                    <h3 className="font-medium text-charcoal mb-2">Start planning this adventure!</h3>
                    <p className="text-sm text-muted-foreground">
                      Add activities, photos, and comments to bring this dream to life.
                    </p>
                  </div>
                )}

                {/* Notes Section */}
                {summary.item.notes && (
                  <section className="p-4 bg-accent/10 rounded-xl">
                    <h3 className="text-sm font-semibold text-charcoal mb-2">Notes</h3>
                    <p className="text-sm text-muted-foreground">{summary.item.notes}</p>
                  </section>
                )}

                {/* Share & Export Section */}
                <section className="pt-4 border-t border-charcoal/10 space-y-3">
                  <h3 className="text-sm font-semibold text-charcoal mb-3">Share & Export</h3>
                  <ShareLinkManager itemId={bucketItemId} />
                  <ExportButton type="trip" itemId={bucketItemId} />
                </section>
              </div>
            </div>
          ) : (
            <div className="p-12 text-center text-muted-foreground">
              Failed to load trip summary
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default TripSummary;
